// ============================================================================
// ClientCalendarPanel — ONE client's colour-coded calendar (Wave 8, spec §C).
// ----------------------------------------------------------------------------
// On the trainer's client-detail page, a month calendar scoped to a SINGLE
// client, merging that client's:
//   • PT sessions       (coral)  — ENTRY_STYLE.pt_session
//   • coach homework    (violet) — ENTRY_STYLE.activity_suggested
//   • self-logged work  (lime)   — ENTRY_STYLE.activity_own
//   • per-day meals     (amber)  — ENTRY_STYLE.meal
//   • weekly goal       (lime banner over the week) — ENTRY_STYLE.weekly_goal
// plus a PLANNED-detail list (upcoming sessions + homework + the active goal).
//
// Reuse posture: it wraps its own <DashboardDataProvider> (the same live, API-
// backed trainer feed the consolidated calendar uses) and FILTERS every layer
// to `clientId`, so it shares the exact view-model, ENTRY_STYLE palette and
// bilingual CalendarLegend with the all-clients grid — the two read the same
// colour language — without any new endpoint. Tapping a session opens the shared
// SessionDetailSheet (inline edit); homework/meals open their detail sheets.
//
// Mobile-first, Pulse, EN + 繁中, reduced-motion aware. All copy via i18n keys
// (clientDetail:clientCalendar.* + the shared activity/weeklyGoals namespaces).
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, EmptyState } from "../../components";
import {
  IconChevronLeft,
  IconChevronRight,
  IconMapPin,
} from "../../components/icons";
import {
  addMonths,
  formatDayLong,
  formatTime,
  isSameDay,
  localDayKey,
  monthGrid,
  monthTitle,
  weekdayLabels,
} from "../../lib/date";
import { CalendarLegend } from "../../dashboard/CalendarLegend";
import { ENTRY_STYLE, SESSION_STYLE } from "../../dashboard/sessionStyle";
import { SessionDetailSheet } from "../../dashboard/SessionDetailSheet";
import { ActivityDetailSheet } from "../../dashboard/ActivityDetailSheet";
import { MealMarkerSheet } from "../../dashboard/MealMarkerSheet";
import {
  DashboardDataProvider,
  useDashboardCtx,
} from "../../dashboard/DashboardDataContext";
import type {
  ActivityVM,
  MealMarkerVM,
  SessionVM,
} from "../../dashboard/useDashboardData";
import { LoadingBlock } from "../../app/PageState";
import { cn } from "../../lib/cn";

/** A merged, client-scoped calendar entry, time-keyed by local wall-clock. */
type Entry =
  | { kind: "session"; data: SessionVM; startsAt: string }
  | { kind: "activity"; data: ActivityVM; startsAt: string }
  | { kind: "meal"; data: MealMarkerVM; startsAt: string };

/** Fixed "today" for the render pass (stable cursor/selection). */
const TODAY = (() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
})();

export function ClientCalendarPanel({ clientId }: { clientId: number }) {
  // Self-contained live data provider (shared React Query cache); the inner view
  // filters every layer to this one client.
  return (
    <DashboardDataProvider>
      <ClientCalendarInner clientId={clientId} />
    </DashboardDataProvider>
  );
}

function ClientCalendarInner({ clientId }: { clientId: number }) {
  const { t } = useTranslation("clientDetail");
  const reduce = useReducedMotion();
  const data = useDashboardCtx();

  const [cursor, setCursor] = useState<Date>(() => TODAY);
  const [selected, setSelected] = useState<Date>(() => TODAY);
  const [openSession, setOpenSession] = useState<SessionVM | null>(null);
  const [openActivity, setOpenActivity] = useState<ActivityVM | null>(null);
  const [openMeal, setOpenMeal] = useState<MealMarkerVM | null>(null);

  // ---- Scope every layer of the trainer feed to THIS client. ----
  const sessions = useMemo(
    () => data.sessions.filter((s) => s.clientId === clientId),
    [data.sessions, clientId],
  );
  const activities = useMemo(
    () => data.activities.filter((a) => a.clientId === clientId),
    [data.activities, clientId],
  );
  const meals = useMemo(
    () => data.mealMarkers.filter((m) => m.clientId === clientId),
    [data.mealMarkers, clientId],
  );
  // Monday week_start days ('YYYY-MM-DD') with a coach goal for this client —
  // drives the lime week-level banner over each matching week row.
  const goalWeeks = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const g of data.weeklyGoals) {
      if (g.clientId === clientId && g.weekStart) set.add(g.weekStart);
    }
    return set;
  }, [data.weeklyGoals, clientId]);

  // Merge into one time-sorted, local-day-keyed list.
  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [
      ...sessions.map((s): Entry => ({ kind: "session", data: s, startsAt: s.startsAt })),
      ...activities.map((a): Entry => ({ kind: "activity", data: a, startsAt: a.startsAt })),
      ...meals.map((m): Entry => ({ kind: "meal", data: m, startsAt: m.startsAt })),
    ];
    return out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }, [sessions, activities, meals]);

  const entriesOnDay = (day: Date): Entry[] => {
    const key = localDayKey(day);
    return entries.filter((e) => e.startsAt.startsWith(key));
  };

  const days = useMemo(() => monthGrid(cursor), [cursor]);
  const labels = useMemo(() => weekdayLabels(), []);
  const month = cursor.getMonth();
  const selectedEntries = entriesOnDay(selected);

  // ---- Planned detail: upcoming (future / undated-planned) sessions + homework. ----
  const planned = useMemo(() => {
    const now = Date.now();
    const future = (e: Entry): boolean => {
      const tms = new Date(e.startsAt).getTime();
      return Number.isNaN(tms) || tms >= now - 86_400_000; // include today
    };
    const sessionItems = entries
      .filter((e) => e.kind === "session" && (e.data as SessionVM).status === "planned" && future(e))
      .slice(0, 6);
    const homeworkItems = entries
      .filter((e) => e.kind === "activity" && (e.data as ActivityVM).status === "planned" && future(e))
      .slice(0, 6);
    return { sessionItems, homeworkItems };
  }, [entries]);

  const fade = {
    initial: reduce ? false : { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, ease: [0.22, 0.61, 0.36, 1] as const },
  };

  if (data.isLoading) {
    return (
      <Card>
        <LoadingBlock label={t("clientCalendar.loading")} />
      </Card>
    );
  }
  if (data.isError) {
    return (
      <EmptyState
        title={t("clientCalendar.loadError")}
        action={
          <Button size="sm" onClick={() => data.refetch()}>
            {t("clientCalendar.retry")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <motion.div {...fade}>
        <Card padded={false} className="overflow-hidden">
          {/* Header: month title + nav */}
          <div className="flex items-center justify-between border-b border-line p-4">
            <div>
              <h3 className="font-display text-lg font-extrabold tracking-tightest">
                {monthTitle(cursor)}
              </h3>
              <p className="text-sm text-body-soft">{t("clientCalendar.subtitle")}</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, -1))}
                aria-label={t("clientCalendar.prevMonth")}
                className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
              >
                <IconChevronLeft />
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCursor(TODAY);
                  setSelected(TODAY);
                }}
              >
                {t("clientCalendar.today")}
              </Button>
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, 1))}
                aria-label={t("clientCalendar.nextMonth")}
                className="grid h-9 w-9 place-items-center rounded-pill bg-surface text-body-soft ring-1 ring-line transition-colors hover:bg-surface-sunken hover:text-body"
              >
                <IconChevronRight />
              </button>
            </div>
          </div>

          {/* Shared bilingual legend */}
          <CalendarLegend className="px-4 pt-3" />

          {/* Month grid */}
          <div className="p-3">
            <div className="mb-1.5 grid grid-cols-7 gap-1">
              {labels.map((w, i) => (
                <div
                  key={i}
                  className="px-1 text-center text-[0.62rem] font-bold uppercase tracking-kicker text-body-faint sm:text-[0.68rem]"
                >
                  <span className="sm:hidden">{w.slice(-1)}</span>
                  <span className="hidden sm:inline">{w}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              {Array.from({ length: 6 }, (_, w) => {
                const rowDays = days.slice(w * 7, w * 7 + 7);
                const monday = rowDays[0];
                const weekKey = monday ? localDayKey(monday) : "";
                const hasGoal = weekKey !== "" && goalWeeks.has(weekKey);
                return (
                  <div key={`wk-${weekKey}-${w}`}>
                    {hasGoal && (
                      <div className="mb-1 flex items-center gap-1.5 rounded-pill bg-grad-lime px-2.5 py-1 text-white shadow-sm">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white/90" aria-hidden />
                        <span className="truncate font-display text-[0.68rem] font-bold tracking-tight">
                          {t("weeklyGoals:banner.title")}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-7 gap-1">
                      {rowDays.map((day) => {
                        const items = entriesOnDay(day);
                        const inMonth = day.getMonth() === month;
                        const isToday = isSameDay(day, TODAY);
                        const isSelected = isSameDay(day, selected);
                        return (
                          <button
                            key={day.toISOString()}
                            type="button"
                            onClick={() => setSelected(day)}
                            aria-pressed={isSelected}
                            aria-label={t("clientCalendar.dayLabel", {
                              day: formatDayLong(day),
                              count: items.length,
                            })}
                            className={cn(
                              "flex min-h-[3.6rem] flex-col items-center rounded-md p-1 transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt",
                              inMonth ? "bg-surface ring-1 ring-line" : "bg-surface-sunken/40 ring-1 ring-line-soft",
                              isSelected ? "ring-2 ring-coral shadow-volt" : "hover:ring-line-strong",
                            )}
                          >
                            <span
                              className={cn(
                                "grid h-6 min-w-[1.5rem] place-items-center rounded-pill px-1 text-xs font-bold tabular-nums",
                                isToday
                                  ? "bg-grad-coral text-white shadow-volt"
                                  : inMonth
                                    ? "text-body"
                                    : "text-body-faint",
                              )}
                            >
                              {day.getDate()}
                            </span>
                            <span className="mt-auto flex flex-wrap justify-center gap-0.5 pt-1">
                              {items.slice(0, 4).map((e) => (
                                <span
                                  key={`${e.kind}${e.data.id}`}
                                  className={cn(
                                    "h-1.5 w-1.5 rounded-full",
                                    e.kind === "session"
                                      ? SESSION_STYLE[e.data.type].dot
                                      : e.kind === "meal"
                                        ? ENTRY_STYLE.meal.dot
                                        : ENTRY_STYLE[e.data.kind].dot,
                                  )}
                                />
                              ))}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected-day list */}
          <div className="border-t border-line bg-surface-soft p-4">
            <h4 className="mb-3 font-display text-base font-extrabold tracking-tight">
              {isSameDay(selected, TODAY) ? `${t("clientCalendar.today")} · ` : ""}
              {formatDayLong(selected)}
            </h4>
            {selectedEntries.length === 0 ? (
              <p className="rounded-md bg-surface px-4 py-6 text-center text-sm text-body-muted ring-1 ring-line">
                {t("clientCalendar.emptyDay")}
              </p>
            ) : (
              <ul className="space-y-2">
                {selectedEntries.map((e) =>
                  e.kind === "session" ? (
                    <SessionRow
                      key={`s${e.data.id}`}
                      session={e.data}
                      onPick={setOpenSession}
                    />
                  ) : e.kind === "meal" ? (
                    <MealRow
                      key={`m${e.data.id}`}
                      marker={e.data}
                      onPick={setOpenMeal}
                    />
                  ) : (
                    <ActivityRow
                      key={`a${e.data.id}`}
                      activity={e.data}
                      onPick={setOpenActivity}
                    />
                  ),
                )}
              </ul>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Planned / upcoming detail */}
      <motion.div {...fade}>
        <Card>
          <div className="mb-3">
            <h3 className="font-display text-lg font-extrabold tracking-tightest text-body">
              {t("clientCalendar.plannedTitle")}
            </h3>
            <p className="text-sm text-body-soft">{t("clientCalendar.plannedSubtitle")}</p>
          </div>

          {planned.sessionItems.length === 0 && planned.homeworkItems.length === 0 ? (
            <p className="rounded-md bg-surface-sunken px-4 py-8 text-center text-sm text-body-soft">
              {t("clientCalendar.plannedEmpty")}
            </p>
          ) : (
            <div className="space-y-5">
              {planned.sessionItems.length > 0 && (
                <PlannedGroup title={t("clientCalendar.plannedSessions")}>
                  {planned.sessionItems.map((e) => (
                    <SessionRow
                      key={`ps${e.data.id}`}
                      session={e.data as SessionVM}
                      onPick={setOpenSession}
                      showDate
                    />
                  ))}
                </PlannedGroup>
              )}
              {planned.homeworkItems.length > 0 && (
                <PlannedGroup title={t("clientCalendar.plannedHomework")}>
                  {planned.homeworkItems.map((e) => (
                    <ActivityRow
                      key={`ph${e.data.id}`}
                      activity={e.data as ActivityVM}
                      onPick={setOpenActivity}
                      showDate
                    />
                  ))}
                </PlannedGroup>
              )}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Shared detail sheets (live-synced; reuse the trainer calendar's). */}
      <SessionDetailSheet session={openSession} onClose={() => setOpenSession(null)} />
      <ActivityDetailSheet activity={openActivity} onClose={() => setOpenActivity(null)} />
      <MealMarkerSheet marker={openMeal} onClose={() => setOpenMeal(null)} />
    </div>
  );
}

function PlannedGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </p>
      <ul className="space-y-2">{children}</ul>
    </div>
  );
}

/** Compact long date+time for planned rows ("Mon 16 Jun · 09:00"). */
function whenLabel(startsAt: string): string {
  const date = formatDayLong(startsAt);
  const time = formatTime(startsAt);
  return time ? `${date} · ${time}` : date;
}

function SessionRow({
  session,
  onPick,
  showDate = false,
}: {
  session: SessionVM;
  onPick: (s: SessionVM) => void;
  showDate?: boolean;
}) {
  const { t } = useTranslation("activity");
  const st = ENTRY_STYLE.pt_session;
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(session)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body tabular-nums">
              {showDate ? whenLabel(session.startsAt) : (session.startsAt ? formatTime(session.startsAt) : "")}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {t("provenance.pt_session")}
            </Badge>
          </div>
          {session.location && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-body-soft">
              <IconMapPin width={13} height={13} />
              <span className="truncate">{session.location}</span>
            </div>
          )}
        </div>
        <Badge tone={session.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
          {session.status === "completed"
            ? t("detail.statusCompleted")
            : t("detail.statusPlanned")}
        </Badge>
      </button>
    </li>
  );
}

function ActivityRow({
  activity,
  onPick,
  showDate = false,
}: {
  activity: ActivityVM;
  onPick: (a: ActivityVM) => void;
  showDate?: boolean;
}) {
  const { t } = useTranslation("activity");
  const st = ENTRY_STYLE[activity.kind];
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(activity)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {activity.title || activity.type || t(`provenance.${activity.kind}`)}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {t(`provenance.${activity.kind}`)}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-body-soft tabular-nums">
            {showDate
              ? whenLabel(activity.startsAt)
              : activity.startsAt
                ? formatTime(activity.startsAt)
                : ""}
            {activity.type ? ` · ${activity.type}` : ""}
          </div>
        </div>
        <Badge tone={activity.status === "completed" ? "success" : "neutral"} dot className="shrink-0">
          {activity.status === "completed"
            ? t("detail.statusCompleted")
            : t("detail.statusPlanned")}
        </Badge>
      </button>
    </li>
  );
}

function MealRow({
  marker,
  onPick,
}: {
  marker: MealMarkerVM;
  onPick: (m: MealMarkerVM) => void;
}) {
  const { t } = useTranslation("nutrition");
  const st = ENTRY_STYLE.meal;
  const kcal = Math.round(marker.kcal);
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(marker)}
        className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm hover:ring-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className={cn("h-10 w-1.5 shrink-0 rounded-pill", st.grad)} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-display text-sm font-bold text-body">
              {t("marker.title")}
            </span>
            <Badge tone={st.tone} className="shrink-0">
              {marker.count > 0 ? t("marker.mealCount", { count: marker.count }) : t("marker.title")}
            </Badge>
          </div>
        </div>
        <span className="shrink-0 font-display text-sm font-extrabold tabular-nums text-body">
          {kcal.toLocaleString()}{" "}
          <span className="text-xs font-bold text-body-soft">{t("macro.kcalUnit")}</span>
        </span>
      </button>
    </li>
  );
}
